<script>
import { DateTime } from 'luxon'
import FloatingTooltip from '@/components/ui/FloatingTooltip.vue'
import orderBy from 'lodash/orderBy'
import FormattingHelpers from '@/mixins/FormattingHelpers'
import { calculatePurchasingPrice, calculatePurchasingPriceWithNetListPrice } from '@/api/cars'
import Spinner from '@/components/ui/loader/Spinner.vue'

export default {
  components: {
    'mia-floating-tooltip': FloatingTooltip,
    'mia-spinner': Spinner
  },

  mixins: [FormattingHelpers],

  props: {
    car: {
      type: Object,
      required: true
    },

    mode: {
      type: String,
      default: 'car'
    },

    make: {
      type: String,
      default: ''
    },

    model: {
      type: String,
      default: ''
    },

    netListPrice: {
      type: [String, Number],
      default: ''
    },

    withoutSource: {
      type: Boolean,
      default: false
    },

    withoutHistory: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      showTooltip: false,
      isCalculating: false,
      originalPurchasingPrice: this.car.item_price_purchased || ''
    }
  },

  computed: {
    localizedSource() {
      return this.getLocalizedSource(this.car.item_price_purchased_last_source)
    },

    lastChangeInfo() {
      if (!this.car.item_price_purchased_last_changed_at && !this.car.item_price_purchased_last_changed_by) {
        return ''
      }

      let str = 'Zuletzt geändert'

      if (this.car.item_price_purchased_last_changed_at) {
        str = `${str} am ${DateTime.fromSQL(this.car.item_price_purchased_last_changed_at).toFormat('dd.MM.yyyy \'um\' HH:mm \'Uhr\'')}`
      }

      if (this.car.item_price_purchased_last_changed_by) {
        str = `${str} von ${this.car.item_price_purchased_last_changed_by_firstname} ${this.car.item_price_purchased_last_changed_by_lastname}`
      }

      return str
    },

    sortedHistory() {
      return orderBy(this.car.item_price_purchased_history, item => DateTime.fromISO(item.timestamp), ['desc'])
    }
  },

  methods: {
    onClickOutside(event) {
      if (event.target === this.$refs.historyButtonRef) {
        return
      }

      this.showTooltip = false
    },

    apply(item) {
      this.$emit('apply', { value: item.value, source: 'manual' })
      this.showTooltip = false
    },

    async calculatePrice() {
      if (this.isCalculating) {
        return
      }

      this.isCalculating = true
      let error = null
      let response = null

      if (this.mode === 'car') {
        [error, response] = await calculatePurchasingPrice(this.car.id)
      } else {
        [error, response] = await calculatePurchasingPriceWithNetListPrice(this.make, this.model, this.netListPrice)
      }

      this.isCalculating = false

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Der Einkaufspreis konnte nicht berechnet werden']
        })
        return
      }

      this.$emit('apply', { value: response.data.item_purchasing_price, source: 'calculator' })
      window.eventBus.$emit('mia-info', {
        info: ['Einkaufspreis erfolgreich berechnet']
      })
    },

    getLocalizedSource(source) {
      return {
        invoice: 'Rechnungsimport',
        manual: 'Manuelle Eingabe',
        calculator: 'EK-Preis Kalkulator',
        initial: 'Ursprünglicher Preis'
      }[source] || 'Unbekannt'
    }
  }
}
</script>

<template>
  <div>
    <div
      v-if="lastChangeInfo"
      class="text-sm italic"
    >
      {{ lastChangeInfo }}
    </div>

    <div class="mt-2 flex justify-between">
      <div
        v-if="!withoutSource"
        class="text-sm"
      >
        <span class="font-semibold">Herkunft</span>: {{ localizedSource }}
      </div>
      <div v-else />

      <div class="relative">
        <button
          type="button"
          class="text-orange-mia hover:underline focus:outline-none"
          @click="calculatePrice"
        >
          Einkaufspreis berechnen
        </button>

        <div
          v-if="isCalculating"
          class="absolute inset-0 flex items-center bg-white justify-end"
        >
          <mia-spinner class="text-orange-mia w-5 h-5 mr-2" />
          <span class="italic">wird berechnet...</span>
        </div>
      </div>
    </div>

    <div
      v-if="!withoutHistory"
      class="mt-2 flex justify-end"
    >
      <div v-if="car.item_price_purchased_history && Array.isArray(car.item_price_purchased_history) && car.item_price_purchased_history.length > 0">
        <mia-floating-tooltip
          :triggers="[]"
          :shown="showTooltip"
          :delay="{ show: 0, hide: 0 }"
          placement="bottom"
          :auto-hide="false"
        >
          <button
            ref="historyButtonRef"
            type="button"
            class="text-orange-mia hover:underline focus:outline-none"
            @click="showTooltip = !showTooltip"
          >
            Historie ({{ car.item_price_purchased_history.length }})
          </button>

          <template #popper>
            <div v-click-outside="onClickOutside">
              <div
                v-for="(item, index) in sortedHistory"
                :key="item.timestamp"
                :class="index > 0 ? '-mt-5' : ''"
              >
                <div class="flex space-x-4 items-start">
                  <button
                    class="font-mono w-5/12 text-left focus:outline-none hover:text-orange-mia hover:underline"
                    :class="index === 0 ? 'text-orange-mia font-semibold' : ''"
                    type="button"
                    @click="apply(item)"
                  >
                    {{ formatAsCurrency(item.value) }}
                  </button>
                  <div class="relative transform -translate-y-px">
                    <div class="font-semibold whitespace-no-wrap">
                      {{ parseAsIsoDate(item.timestamp).toFormat('dd.MM.yyyy, HH:mm \'Uhr\'') }}
                    </div>
                    <div class="whitespace-no-wrap text-sm">
                      <span class="italic">via </span> <span>{{ getLocalizedSource(item.source) }}</span>
                    </div>
                    <div class="whitespace-no-wrap text-sm">
                      <span class="italic">von </span> <router-link
                        v-if="item.user"
                        :to="`/users/${item.user}/profile`"
                        target="_blank"
                        class="hover:underline"
                      >
                        {{ item.item_price_purchased_last_changed_by_firstname }} {{ item.item_price_purchased_last_changed_by_lastname }}
                      </router-link>
                      <span v-else>Unbekannt</span>
                    </div>
                  </div>
                </div>
                <div
                  v-if="index < car.item_price_purchased_history.length - 1"
                  class="text-gray-800 relative transform -translate-y-8 flex flex-col pointer-events-none"
                >
                  <div>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="w-5 h-5"
                    ><path d="m5 9 7-7 7 7" /><path d="M12 16V2" /><circle
                      cx="12"
                      cy="21"
                      r="1"
                    /></svg>
                  </div>
                  <div class="h-8">
                    <div
                      class="h-full border-l-2 border-gray-800 border-dotted relative"
                      style="transform: translateX(9px)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </template>
        </mia-floating-tooltip>
      </div>
      <div
        v-else
        class="text-sm italic"
      >
        ohne Historie
      </div>
    </div>
  </div>
</template>
